import { type SEARCH_FILTER_TYPE } from '@/features/shared/constants/global'
import { type ProductAPI, type Product } from '@/features/shop/services/Product/types'
import { type Coupon, type CouponAPI } from '@/features/shop/services/Coupons/types'
import { type Shelf } from '@/features/shop/services/Shelves/types'

export type EndcapBySlotPos = Record<string | number, Endcap>

export type GeneralFacetItem = {
  name: string
  prevValue?: number[]
}
interface GeneralFacet {
  name: string
  facet: Exclude<SEARCH_FILTER_TYPE, 'categories'>
  items: GeneralFacetItem[]
}
interface CategoryFacetAPI {
  name: string
  facet: SEARCH_FILTER_TYPE.CATEGORY
  items: CategoryFacetItemAPI[]
}
export type FacetAPI = CategoryFacetAPI | GeneralFacet
export type Facet = CategoryFacet | GeneralFacet

export interface Endcap {
  id: number
  title: string
  image_background_url: string
  click_uri: string
  slot_position: number
  slot_size: number
  action_text: string
  version: number
  view_scope_id: number
  category_id: number
  category_name: string
  promotion_id?: number | string
  featured?: boolean
  featured_transaction_id?: string
  beacons?: EndcapBeacon
  row_position?: number
  banner_id: string
  source?: string
  design_type?: string
  metadata?: {
    tag_line?: string
    link_text?: string
    foreground_image?: string
    show_link_button?: boolean
  }
  ad_version: 'v1' | 'v2'
}

export interface CategoryFacetItemAPI {
  name: string
  id: number
}
export interface CategoryFacet {
  name: string
  facet: SEARCH_FILTER_TYPE.CATEGORY
  items: SearchCategory[]
}
export interface SearchCategory extends CategoryFacetItemAPI {
  parentName?: string
  parentId?: number
  lineageDepth: number
  lineage: string
}

export interface Beacons {
  onViewBeacon?: string
  onLoadBeacon?: string
  onClickBeacon?: string
  onBasketChangeBeacon?: string
  featuredTransactionID?: string
}

interface EndcapBeacon {
  on_view_beacon: string
  on_click_beacon: string
  on_load_beacon?: string
}

export interface MoreLikeThisSearchResponse {
  hits: ProductAPI[]
}

export interface SearchResponse {
  facets: FacetAPI[]
  total_hits: number
  hits_per_page: number
  page: number
  beacons: Beacons
  endcaps: Endcap[]
  hits: ProductAPI[] | null
  coupons: null | CouponAPI[]
  experiment_variant?: Record<string, number>
  shelves?: Shelf<Product>[]
  sort: string
  sort_options: SortOptions[]
}

export interface GlobalSearchResponse {
  total_retailer_hits: number
  size_per_store: number
  retailer_hits?: RetailerHit[]
  metadata?: {
    status: 'redirect'
    redirect_url: string
  }
}

export interface RetailerHit {
  name: string
  metadata: RetailerMetadata
  items: Product[]
  total: number
}

interface RetailerMetadata {
  store_id: number
  metro_id: number
  location_id: number
}

export interface SortOptions {
  name: string
  value: string
}

export interface SearchResults {
  endcapsBySlotPos: EndcapBySlotPos
  products: Product[] | undefined
  total_hits: number
  hits_per_page: number
  page: number
  shelves: Shelf<Product>[]
  beacons?: Beacons
  facets: Facet[]
  coupons: Coupon[]
  totalPages?: number
  metadata?: {
    status: 'redirect'
    redirect_url: string
  }
  product_beacons?: {
    onLoadBeacons?: string[]
  }
  experiment_variant?: Record<string, number>
  sort?: string
  sort_options?: SortOptions[]
}

export interface SearchCouponsResponseLegacy {
  coupons: CouponAPI[]
  coupon_after_key: string
  coupons_per_page: number
  facets: FacetAPI[]
  page: number
  total_coupons: number
}
export type SearchCouponsLegacy = Omit<SearchCouponsResponseLegacy, 'coupons' | 'facets'> & {
  coupons: Coupon[]
  facets: Facet[]
}

export interface CouponFacetOption {
  count: number
  id: string
  name: string
}

export interface CouponFacetResponse {
  id: string
  name: string
  options: CouponFacetOption[]
}

export interface SearchCouponsResponse {
  facets: CouponFacetResponse[]
  result: CouponAPI[] | null
  pagination: {
    after_cursor: string
    total_items: number
  }
}
export type SearchCoupons = Omit<SearchCouponsResponse, 'result' | 'facets'> & {
  result: Coupon[]
  facets: Facet[]
}

export type SearchParams = {
  zip?: string
  query?: string
  sort?: string
  brands?: string[]
  flavors?: string[]
  new_deals?: boolean
  active_deals?: boolean
  coupon_only?: boolean
  category_id?: number
  promotion_category_id?: number
  contexts?: string[]
  category_ids?: string[]
  category_name?: string
  buy_again_query?: string
}

export enum SearchType {
  SEARCH = 'search',
  BUY_AGAIN = 'buy_again',
  UNIVERSAL = 'universal',
}

export type SearchProductProperties = Pick<
  ProductAPI,
  | 'aisle'
  | 'allergen_info'
  | 'bogo'
  | 'brand_name'
  | 'bullet_points'
  | 'categories'
  | 'choking_hazard'
  | 'cpg_promotions'
  | 'description'
  | 'description_label'
  | 'display_name'
  | 'has_custom_label'
  | 'hierarchical_categories'
  | 'highlights'
  | 'image'
  | 'ingredients'
  | 'is_snap_ebt_eligible'
  | 'line_item_promotion'
  | 'low_stock'
  | 'name'
  | 'nmu_eligible'
  | 'nutrition_attributes_for_you'
  | 'nutritional_details'
  | 'on_sale'
  | 'oos'
  | 'original_price'
  | 'parent_product_id'
  | 'price'
  | 'pricing'
  | 'product_id'
  | 'product_type'
  | 'purchase_limit'
  | 'review_stats'
  | 'sale_price'
  | 'secondary_images'
  | 'size'
  | 'specifications'
  | 'state'
  | 'state_reason'
  | 'store_id'
  | 'store_location_id'
  | 'unit_of_measure'
  | 'unit_weight'
  | 'universal_id'
  | 'upcs'
  | 'variation_display_name'
  | 'variation_properties'
>

export type SearchProductMetadata = Pick<ProductAPI, 'bought' | 'boost_bought' | 'for_you'> & {
  purchase_origin?: 'retailer' | 'cross_retailer'
}

export type SearchProductAdsProperties = Pick<
  ProductAPI,
  'beacons' | 'featured' | 'featured_source' | 'featured_transaction_id'
>

export type SearchProductVariantValue = {
  id: string
  color?: ProductAPI['variation_color']
  size?: ProductAPI['variation_size']
  swatch_url?: ProductAPI['swatch_url']
}

export type SearchProductColorVariant = {
  name: string
  swatch_url?: ProductAPI['swatch_url']
}

export type SearchProductSizeVariant = {
  name: string
}

export type SearchProductVariants = {
  values?: SearchProductVariantValue[]
  colors: {
    total: number
    variants: SearchProductColorVariant[]
  }
  sizes: {
    total: number
    variants: SearchProductSizeVariant[]
  }
  group_id: string
}

export type SearchProductAPI = {
  id: string
  properties: SearchProductProperties
  metadata?: SearchProductMetadata
  ads_properties?: SearchProductAdsProperties
  variants: SearchProductVariants
}
